import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, BookOpen, Book, Heart, Car, ShoppingCart, Coffee, Clock, Zap, MapPin, Search, Building2, FileText, Hospital, Shield } from 'lucide-react';
import { MobileFooterNav } from './MobileFooterNav';
import { DataSourceAttribution } from './DataSourceAttribution';

interface ChapterCardProps {
  chapter: number;
  title: string;
  description: string;
  icon: React.ReactNode;
  isActive: boolean;
  comingSoon?: boolean;
  onClick?: () => void;
}

const ChapterCard = ({ chapter, title, description, icon, isActive, comingSoon, onClick }: ChapterCardProps) => {
  // Special enhanced card for Chapter 1 (Hospitals) with real data
  if (chapter === 1 && isActive) {
    return (
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 shadow-lg hover:shadow-xl transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                {icon}
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-emerald-800">Chapter {chapter}: {title}</CardTitle>
                <Badge className="bg-emerald-600 text-white text-xs font-medium mt-1">
                  ACTIVE - Real Data
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-xs mb-4 leading-relaxed text-gray-800 font-medium">
            {description}
          </CardDescription>

          {/* Hospital Data Stats */}
          <div className="bg-white/50 rounded-lg p-3 mb-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-gray-900 font-medium">
                <Building2 className="h-3 w-3 text-emerald-600" />
                Hospitals Mapped:
              </span>
              <span className="font-bold text-emerald-700">1,585/6,090 (26%)</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-gray-900 font-medium">
                <FileText className="h-3 w-3 text-emerald-600" />
                Procedure Records:
              </span>
              <span className="font-bold text-emerald-700">204.6M prices</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-gray-900 font-medium">
                <MapPin className="h-3 w-3 text-emerald-600" />
                Coverage:
              </span>
              <span className="font-bold text-emerald-700">All 50 States + DC</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-4">
            <Link to="/search">
              <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                <Search className="h-3 w-3 mr-2" />
                Search Hospitals & Procedures
              </Button>
            </Link>
            <Link to="/map">
              <Button size="sm" variant="outline" className="w-full border-emerald-600 text-emerald-700 hover:bg-emerald-50 bg-white">
                <MapPin className="h-3 w-3 mr-2" />
                View Hospital Map
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Special enhanced card for Chapter 2 (Insurance) with medical condition CTAs
  if (chapter === 2 && comingSoon) {
    const medicalConditions = [
      { name: "Organ Transplants", range: "$50K-$400K difference" },
      { name: "Genetic Therapies", range: "$25K-$200K difference" },
      { name: "Kidney Dialysis", range: "$15K-$75K difference" },
      { name: "Cancer Treatments", range: "$30K-$250K difference" },
      { name: "Immune Disorders", range: "$10K-$100K difference" }
    ];

    return (
      <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 shadow-md hover:shadow-lg transition-all duration-300">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                {icon}
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-gray-900">Chapter {chapter}: {title}</CardTitle>
                <Badge variant="outline" className="mt-1 text-xs border-gray-300 text-gray-600">
                  <Clock className="h-3 w-3 mr-1" />
                  Coming Soon
                </Badge>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <CardDescription className="text-xs mb-3 leading-relaxed text-gray-800 font-medium">
            {description}
          </CardDescription>

          {/* Medical Condition CTA Buttons */}
          <div className="grid grid-cols-1 gap-1 mb-3">
            {medicalConditions.map((condition, index) => (
              <Button
                key={index}
                size="sm"
                variant="outline"
                className="text-xs h-10 border-blue-200 text-blue-700 hover:bg-blue-50 bg-white justify-between px-3"
                disabled
              >
                <span className="flex items-center">
                  <Shield className="h-2 w-2 mr-2" />
                  {condition.name}
                </span>
                <span className="text-red-600 font-bold text-xs">
                  {condition.range}
                </span>
              </Button>
            ))}
          </div>

          <Button disabled size="sm" className="w-full bg-gray-100 text-gray-500 cursor-not-allowed">
            Coming Soon...
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Standard card for other chapters
  return (
    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 shadow-md hover:shadow-lg transition-all duration-300">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              {icon}
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-gray-900">Chapter {chapter}: {title}</CardTitle>
              {comingSoon && (
                <Badge variant="outline" className="mt-1 text-xs border-gray-300 text-gray-600">
                  <Clock className="h-3 w-3 mr-1" />
                  Coming Soon
                </Badge>
              )}
            </div>
          </div>
          {isActive && (
            <Badge className="bg-emerald-600 text-white text-xs font-medium">
              ACTIVE
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <CardDescription className="text-xs mb-3 leading-relaxed text-gray-800 font-medium">
          {description}
        </CardDescription>
        {isActive && onClick && (
          <Button onClick={onClick} size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
            Start Chapter
            <ArrowRight className="h-3 w-3 ml-2" />
          </Button>
        )}
        {comingSoon && (
          <Button disabled size="sm" className="w-full bg-gray-100 text-gray-500 cursor-not-allowed">
            Coming Soon...
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

interface AntiGreedGuidebookProps {
  backgroundVideoUrl?: string;
}

export const AntiGreedGuidebook: React.FC<AntiGreedGuidebookProps> = ({ backgroundVideoUrl }) => {
  const [isGuideOpen, setIsGuideOpen] = useState(false);

  const handleOpenGuide = () => {
    setIsGuideOpen(true);
  };

  const handleChapterSelect = (chapter: number) => {
    if (chapter === 1) {
      // Navigate to search page for hospitals
      window.location.href = '/search';
    }
  };

  const chapters = [
    {
      chapter: 1,
      title: "Hospitals",
      description: "Navigate medical billing madness. Compare prices, decode insurance, and survive your next ER visit without going bankrupt.",
      icon: <Hospital className="h-5 w-5 text-emerald-600" />,
      isActive: true,
    },
    {
      chapter: 2,
      title: "Insurance Plans",
      description: "Compare real insurance costs for YOUR medical condition. We've analyzed every hospital's negotiated rates with each insurer, revealing which plans will cost you the most out-of-pocket. Choose your insurance based on actual pricing data for:",
      icon: <Shield className="h-5 w-5 text-gray-500" />,
      isActive: false,
      comingSoon: true,
    },
    {
      chapter: 3,
      title: "Gas",
      description: "Find the least greedy gas stations in your area. Because every penny counts when they're gouging you at the pump.",
      icon: <Car className="h-5 w-5 text-gray-500" />,
      isActive: false,
      comingSoon: true,
    },
    {
      chapter: 4,
      title: "Taxes",
      description: "Navigate the tax preparation industrial complex. Discover which services actually save you money versus those that just drain your wallet.",
      icon: <FileText className="h-5 w-5 text-gray-500" />,
      isActive: false,
      comingSoon: true,
    },
    {
      chapter: 5,
      title: "Fast Food",
      description: "When you're too tired to cook but don't want to get completely ripped off. Find the best bang for your burger buck.",
      icon: <Coffee className="h-5 w-5 text-gray-500" />,
      isActive: false,
      comingSoon: true,
    },
  ];

  if (!isGuideOpen) {
    // Book Cover
    return (
      <div className="relative flex items-center justify-center min-h-screen px-4 pb-24 overflow-hidden" style={{ background: 'transparent' }}>
        {/* Video Background */}
        {backgroundVideoUrl && (
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full object-cover md:h-[calc(100%-150px)] h-full"
            style={{
              mixBlendMode: 'multiply',
              opacity: 1
            }}
          >
            <source src={backgroundVideoUrl} type="video/mp4" />
          </video>
        )}

        {/* Fallback animated gradient background */}
        {!backgroundVideoUrl && (
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900 via-teal-800 to-green-900 animate-gradient-slow" />
        )}

        {/* Radial gradient overlay for depth */}
        <div className="absolute inset-0 bg-radial-gradient from-transparent via-emerald-900/30 to-emerald-950/80" />

        {/* Book Cover */}
        <div className="book-cover w-full max-w-md h-96 rounded-lg p-8 flex flex-col justify-between relative overflow-hidden z-10 bg-gradient-to-br from-emerald-800/80 to-emerald-900/80 border border-emerald-600/20 shadow-2xl">
          {/* Decorative corner elements */}
          <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-yellow-400/30" />
          <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-yellow-400/30" />
          <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-yellow-400/30" />
          <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-yellow-400/30" />

          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <Book className="h-12 w-12 text-white/80" />
            </div>

            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white leading-tight font-serif">
                The Anti-Greed<br />Guide
              </h1>
              <p className="text-white/80 mt-3 text-sm italic">
                Your guide to surviving Late Stage Capitalism
              </p>
            </div>
          </div>

          <div className="text-center">
            <Button
              onClick={handleOpenGuide}
              className="bg-white text-emerald-900 hover:bg-gray-100 shadow-lg font-medium"
              size="lg"
            >
              <Zap className="h-4 w-4 mr-2" />
              Open the Guide
            </Button>
          </div>

          {/* Subtle texture overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent pointer-events-none" />
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-amber-200/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-emerald-200/20 rounded-full blur-3xl" />
        </div>

        {/* Book-cover state is what the homepage actually lands on, so the
            attribution has to live here too — not only on the contents view. */}
        <DataSourceAttribution />

        {/* Mobile Footer Navigation */}
        <MobileFooterNav />
      </div>
    );
  }

  // Table of Contents
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-100/80 via-orange-100/70 to-yellow-100/80 py-4 pb-20 relative overflow-hidden">
      {/* Video Background */}
      {backgroundVideoUrl && (
        <video
          autoPlay
          loop
          muted
          playsInline
          className="absolute inset-0 w-full h-full object-cover opacity-30"
          style={{
            mixBlendMode: 'soft-light'
          }}
        >
          <source src={backgroundVideoUrl} type="video/mp4" />
        </video>
      )}

      {/* Fallback animated gradient */}
      {!backgroundVideoUrl && (
        <div className="absolute inset-0 bg-gradient-to-br from-amber-200/20 via-orange-200/20 to-yellow-200/20 animate-gradient-slow" />
      )}

      {/* Gradient overlay for better readability */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-amber-50/50 to-orange-50/60" />

      <div className="container mx-auto px-4 h-full relative z-10">
        {/* Book Header - fixed at top left */}
        <div className="fixed top-4 left-4 z-30">
          <div className="flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-emerald-700" />
            <div>
              <h2 className="text-3xl font-bold font-serif text-emerald-800">Anti-Greed Guide</h2>
              <p className="text-sm text-gray-700 italic">
                Your guide to surviving late stage capitalism
              </p>
            </div>
          </div>
        </div>

        {/* Open Book Layout - reduced height */}
        <div className="max-w-7xl mx-auto relative mt-16">
          <div className="open-book relative bg-gradient-to-br from-amber-50 via-yellow-50 to-orange-50 border-2 border-amber-200 shadow-2xl rounded-2xl w-full" style={{ height: 'calc(100vh - 140px)', maxHeight: 'calc(100vh - 375px)', minHeight: '600px' }}>

            {/* Book spine */}
            <div className="absolute left-1/2 top-0 bottom-0 w-6 -ml-3 z-20 bg-gradient-to-b from-emerald-800 via-emerald-600 to-emerald-800 border-l-2 border-r-2 border-gray-300 shadow-xl rounded-sm">
              <div className="absolute top-8 left-1/2 -translate-x-1/2 text-sm text-white font-bold transform -rotate-90 origin-center whitespace-nowrap">
                ANTI-GREED
              </div>
            </div>

            {/* Left and Right Pages */}
            <div className="grid grid-cols-1 lg:grid-cols-2 h-full">
              {/* Left Page */}
              <div className="book-page relative p-8 lg:p-10 flex flex-col bg-gradient-to-br from-yellow-50 to-orange-100">
                {/* Page corners */}
                <div className="absolute top-6 left-6 w-8 h-8 border-l-2 border-t-2 border-gray-300 z-10"></div>
                <div className="absolute bottom-6 left-6 w-8 h-8 border-l-2 border-b-2 border-gray-300 z-10"></div>

                <div className="flex-1 flex flex-col justify-between relative z-10">
                  <div>
                    <h3 className="text-2xl font-semibold mb-4 text-center text-emerald-700 font-serif">Part I: Medical Mayhem</h3>
                    <div className="space-y-4">
                      <ChapterCard
                        {...chapters[0]}
                        onClick={() => handleChapterSelect(chapters[0].chapter)}
                      />
                      <ChapterCard
                        {...chapters[1]}
                        onClick={() => handleChapterSelect(chapters[1].chapter)}
                      />
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <p className="text-xs text-gray-500 italic">
                      "When in doubt, follow the money..."
                    </p>
                  </div>
                </div>

                {/* Page border */}
                <div className="absolute inset-4 border-2 border-gray-200/20 pointer-events-none rounded-lg"></div>
              </div>

              {/* Right Page */}
              <div className="book-page relative p-8 lg:p-10 flex flex-col bg-gradient-to-br from-yellow-50 to-orange-100">
                {/* Page corners */}
                <div className="absolute top-6 right-6 w-8 h-8 border-r-2 border-t-2 border-gray-300 z-10"></div>
                <div className="absolute bottom-6 right-6 w-8 h-8 border-r-2 border-b-2 border-gray-300 z-10"></div>

                <div className="flex-1 flex flex-col justify-between relative z-10">
                  <div>
                    <h3 className="text-2xl font-semibold mb-4 text-center text-emerald-700 font-serif">Part II: Daily Necessities</h3>
                    <div className="space-y-4">
                      <ChapterCard
                        {...chapters[2]}
                        onClick={() => handleChapterSelect(chapters[2].chapter)}
                      />
                      <ChapterCard
                        {...chapters[3]}
                        onClick={() => handleChapterSelect(chapters[3].chapter)}
                      />
                      <ChapterCard
                        {...chapters[4]}
                        onClick={() => handleChapterSelect(chapters[4].chapter)}
                      />
                    </div>
                  </div>

                  <div className="text-center mt-4">
                    <p className="text-xs text-gray-500 italic">
                      "...and always read the fine print"
                    </p>
                  </div>
                </div>

                {/* Page border */}
                <div className="absolute inset-4 border-2 border-gray-200/20 pointer-events-none rounded-lg"></div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <DataSourceAttribution />

      {/* Mobile Footer Navigation */}
      <MobileFooterNav />
    </div>
  );
};